import { Navigate, useLocation } from 'react-router-dom';

/*
 * The addresses the analysis workbench replaced (docs/ANALYSIS_WORKBENCH.md
 * section 8), and the redirect that keeps every link to them working.
 *
 * Each old page becomes one mode of /tools/analysis. Its query is kept as it
 * was, but for the params the workbench itself owns: `mode` (which now names
 * the workbench mode) and `drawers`. A param an old page used under one of
 * those names is renamed (culture's ?mode=tray is tray=1); any other value of
 * them is dropped, as the old page ignored it. The hash is kept.
 */
export const WORKBENCH_PATH = '/tools/analysis';
const SHELL_PARAMS = ['mode', 'drawers'];

export const LEGACY = Object.freeze({
  culture: {
    path: '/tools/culture-analysis',
    // The old tray mode: ?mode=tray.
    rename: { mode: { tray: ['tray', '1'] } },
  },
  // The thermal station (part 2): ?isolate=&capture= kept as they were.
  thermal: { path: '/tools/thermal' },
  // Spore analysis (part 3): ?image= and ?isolate= kept as they are (the game is /game/microscope).
  microscopy: { path: '/tools/spore-analysis' },
});

/**
 * The workbench address (path + search + hash) for an old page's query:
 * `mode` first, then the old params in their order, renamed as LEGACY says.
 */
export function legacyToWorkbench(mode, search = '', hash = '') {
  const old = new URLSearchParams(search);
  const rename = LEGACY[mode]?.rename || {};
  const out = new URLSearchParams();
  out.set('mode', mode);
  for (const [key, value] of old) {
    if (SHELL_PARAMS.includes(key)) {
      const to = rename[key]?.[value];
      if (to) out.set(to[0], to[1]);
      continue;
    }
    out.append(key, value);
  }
  const tail = hash && hash !== '#' ? (hash.startsWith('#') ? hash : `#${hash}`) : '';
  return `${WORKBENCH_PATH}?${out}${tail}`;
}

/** An old tool's address, sent on to its workbench mode with its query (App.js). */
export function MovedToWorkbench({ mode }) {
  const { search, hash } = useLocation();
  return <Navigate to={legacyToWorkbench(mode, search, hash)} replace />;
}

export default MovedToWorkbench;
